// src/pages/sqf/SqfLogDetailPage.js
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import sqfService from '../../services/sqfService';
import companyService from '../../services/companyService';
import { useLanguage, t } from '../../context/LanguageContext';

const displayName = (u) => (u?.firstName || u?.lastName) ? `${u.firstName || ''} ${u.lastName || ''}`.trim() : (u?.username || '—');

// Fetches a fresh presigned GET URL for a visual-inspection photo on mount — the S3 key alone
// (stored in the response) isn't viewable directly, the bucket isn't public.
function VisualInspectionPhoto({ photoKey }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    sqfService.getVisualInspectionPhotoUrl(photoKey)
      .then(u => { if (!cancelled) setUrl(u); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [photoKey]);
  if (failed) return <div className="text-danger" style={{ fontSize: 11 }}>Failed to load photo.</div>;
  if (!url) return <div className="text-muted" style={{ fontSize: 11 }}><span className="spinner-border spinner-border-sm me-1" />Loading photo…</div>;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer">
      <img src={url} alt="Inspection" style={{ maxWidth: 180, maxHeight: 180, borderRadius: 6, border: '1px solid #dee2e6', objectFit: 'cover' }} />
    </a>
  );
}

const QA_BADGE = {
  pending:  { labelEn: 'Pending QA',  labelEs: 'QA Pendiente',  color: 'warning'  },
  approved: { labelEn: 'QA Approved', labelEs: 'Aprobado por QA', color: 'success'  },
  rejected: { labelEn: 'QA Rejected', labelEs: 'Rechazado por QA', color: 'danger'   },
};

export default function SqfLogDetailPage() {
  const { logId } = useParams();
  const { lang } = useLanguage();
  const [log, setLog] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // QA review state
  const [qaAction, setQaAction] = useState(null); // 'approve' | 'reject'
  const [qaNotes, setQaNotes] = useState('');
  const [qaSubmitting, setQaSubmitting] = useState(false);

  // Corrective action state
  const [showCaForm, setShowCaForm] = useState(false);
  const [caFieldId, setCaFieldId] = useState('');
  const [caDescription, setCaDescription] = useState('');
  const [caRootCause, setCaRootCause] = useState('');
  const [caPreventative, setCaPreventative] = useState('');
  const [caSubmitting, setCaSubmitting] = useState(false);

  useEffect(() => {
    Promise.all([
      sqfService.getLog(logId),
      companyService.getCompanies().catch(() => null),
    ]).then(([l, co]) => {
      setLog(l);
      if (co) setCompany(Array.isArray(co) ? co[0] : co);
    }).catch(() => setError(t('Failed to load log.', 'Error al cargar el registro.', lang))).finally(() => setLoading(false));
  }, [logId]); // eslint-disable-line react-hooks/exhaustive-deps

  const reload = () => {
    setLoading(true);
    sqfService.getLog(logId).then(setLog).finally(() => setLoading(false));
  };

  const handleQaSubmit = async () => {
    if (qaAction === 'reject' && !qaNotes.trim()) return;
    setQaSubmitting(true);
    try {
      if (qaAction === 'approve') await sqfService.approveLog(logId, qaNotes);
      else await sqfService.rejectLog(logId, qaNotes);
      setQaAction(null);
      setQaNotes('');
      reload();
    } catch (err) {
      setError(err.response?.data?.message || t('Failed to submit QA decision.', 'Error al enviar la decisión QA.', lang));
    } finally {
      setQaSubmitting(false);
    }
  };

  const handleCaSubmit = async () => {
    if (!caDescription.trim()) return;
    setCaSubmitting(true);
    try {
      await sqfService.createCorrectiveAction({
        logId: parseInt(logId, 10),
        fieldId: caFieldId || null,
        description: caDescription,
        rootCause: caRootCause || null,
        preventativeMeasure: caPreventative || null,
      });
      setShowCaForm(false);
      setCaFieldId(''); setCaDescription(''); setCaRootCause(''); setCaPreventative('');
      reload();
    } catch {
      setError(t('Failed to create corrective action.', 'Error al crear la acción correctiva.', lang));
    } finally {
      setCaSubmitting(false);
    }
  };

  const handleVerify = async (actionId) => {
    try { await sqfService.verifyCorrectiveAction(actionId); reload(); }
    catch { setError(t('Failed to verify corrective action.', 'Error al verificar la acción correctiva.', lang)); }
  };

  if (loading) return (
    <div className="container mt-4 text-center py-5 text-muted">
      <div className="spinner-border spinner-border-sm me-2" />
      {t('Loading…', 'Cargando…', lang)}
    </div>
  );
  if (!log) return (
    <div className="container mt-4">
      <div className="alert alert-danger">{error || t('Log not found.', 'Registro no encontrado.', lang)}</div>
    </div>
  );

  const instance = log.Instance;
  const template = instance?.Template;
  const schema = log.TemplateVersion?.schemaJson || { sections: [] };
  const allFields = (schema.sections || []).flatMap(s => s.fields || []);
  const deviations = (log.Responses || []).filter(r => r.isDeviation);
  const qaCfgRaw = QA_BADGE[log.qaStatus] || { labelEn: log.qaStatus, labelEs: log.qaStatus, color: 'secondary' };
  const qaCfg = { label: t(qaCfgRaw.labelEn, qaCfgRaw.labelEs, lang), color: qaCfgRaw.color };

  // QA gate: check if approval is blocked
  const cas = log.CorrectiveActions || [];
  const unverifiedCas = cas.filter(ca => ca.status !== 'verified');
  const approveBlocked = log.hasDeviation && (cas.length === 0 || unverifiedCas.length > 0);
  const approveBlockReason = cas.length === 0
    ? t(
        'Corrective actions must be documented for all deviations before approving.',
        'Las acciones correctivas deben documentarse para todas las desviaciones antes de aprobar.',
        lang
      )
    : t(
        `${unverifiedCas.length} corrective action${unverifiedCas.length > 1 ? 's are' : ' is'} not yet verified by QA. Verify them before approving.`,
        `${unverifiedCas.length} acción${unverifiedCas.length > 1 ? 'es correctivas no están' : ' correctiva no está'} verificada${unverifiedCas.length > 1 ? 's' : ''} por QA. Verifíquelas antes de aprobar.`,
        lang
      );

  // Print header info
  const checklistDate = instance?.dueDate || log.submittedAt?.split('T')[0] || '';
  const companyAddr = [
    company?.addressLine1,
    company?.addressLine2,
    [company?.city, company?.stateProvince].filter(Boolean).join(', '),
  ].filter(Boolean).join(' · ');

  return (
    <div className="container mt-4 pb-5" id="sqf-log-print">

      {/* ── Print styles ──────────────────────────────────────────────────────── */}
      <style>{`
        @media print {
          .no-print { display: none !important; }

          @page { margin: 10mm 8mm; }

          /* Remove Bootstrap container padding so content uses full page width */
          .container { max-width: 100% !important; padding-left: 0 !important; padding-right: 0 !important; }

          /*
           * <thead> repeats natively on every page in all browsers.
           * <tbody> content always starts directly below the header — no margin tricks needed.
           */
          #print-table { width: 100%; border-collapse: collapse; }
          #print-table thead th {
            padding: 6px 0 6px;
            border-bottom: 2px solid #000;
            font-weight: normal;
          }
          #print-table tbody td {
            vertical-align: top;
            padding-top: 4mm;
          }

          /* Prevent rows from splitting across pages */
          .print-section { page-break-inside: avoid; }
          .print-field-row { page-break-inside: avoid; }
          #print-table tbody tr { page-break-inside: avoid; }

          /* Footer signature block always on last page — avoid splitting */
          #print-footer { page-break-inside: avoid; margin-top: 32px; }

          body { font-size: 11pt; }
        }

        @media screen {
          /* Hide print-only header; flatten only the outermost wrapper row/cell */
          #print-table { display: block; }
          #print-table > thead { display: none; }
          #print-table > tbody { display: block; }
          #print-table > tbody > tr { display: block; }
          #print-table > tbody > tr > td { display: block; padding: 0; border: none; }
        }
      `}</style>

      {/* ── Screen header (outside table — not printed) ──────────────────────── */}
      <div className="d-flex align-items-start justify-content-between mb-3 flex-wrap gap-2 no-print">
        <div>
          <Link to="/sqf/checklists" className="text-muted" style={{ fontSize: 12 }}>← {t('Checklists', 'Listas de verificación', lang)}</Link>
          <h4 className="mb-0 mt-1">{template?.name || t('Checklist Log', 'Registro de Lista de Verificación', lang)}</h4>
          <div className="text-muted" style={{ fontSize: 12 }}>{checklistDate} {instance?.shift !== 'na' ? `· ${instance.shift} shift` : ''}</div>
        </div>
        <button className="btn btn-outline-secondary btn-sm no-print" onClick={() => window.print()}>🖨 {t('Print / Export', 'Imprimir / Exportar', lang)}</button>
      </div>

      {error && <div className="alert alert-danger py-2 mb-3 no-print">{error}</div>}

      {/* ── Print table: <thead> repeats natively on every page ── */}
      <table id="print-table">
        <thead>
          <tr>
            <th>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {/* Left: checklist identity */}
                <div style={{ flex: 1, fontSize: 10 }}>
                  <div style={{ fontWeight: 700, fontSize: 12 }}>{template?.name || t('Checklist', 'Lista de verificación', lang)}</div>
                  {log.TemplateVersion?.versionNumber && (
                    <div>
                      Rev. {log.TemplateVersion.versionNumber}
                      {log.TemplateVersion.revisionDate && (
                        <span> — {new Date(log.TemplateVersion.revisionDate + 'T00:00:00').toLocaleDateString()}</span>
                      )}
                    </div>
                  )}
                  {template?.sqfReference && <div>{template.sqfReference}</div>}
                </div>
                {/* Center: company logo */}
                <div style={{ flex: 1, textAlign: 'center' }}>
                  {company?.logoUrl && (
                    <img src={company.logoUrl} alt="Logo" style={{ maxHeight: 48, maxWidth: 120, objectFit: 'contain' }} />
                  )}
                </div>
                {/* Right: company name + address */}
                <div style={{ flex: 1, textAlign: 'right', fontSize: 10 }}>
                  <div style={{ fontWeight: 700 }}>{company?.name || ''}</div>
                  {companyAddr && <div>{companyAddr}</div>}
                </div>
              </div>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr>
            <td>

        {/* Checklist date + meta — shown in print at top of body */}
        <div className="d-flex flex-wrap gap-3 mb-3 align-items-center">
          <div style={{ fontSize: 13 }}>
            <strong>{t('Date:', 'Fecha:', lang)}</strong> {checklistDate || '—'}
            {instance?.shift && instance.shift !== 'na' && <span className="ms-2 text-capitalize">{instance.shift} shift</span>}
          </div>
          <span className={`badge bg-${qaCfg.color}`}>{qaCfg.label}</span>
          {log.hasDeviation && (
            <span className="badge bg-warning text-dark">
              ⚠ {deviations.length} {deviations.length !== 1 ? t('Deviations', 'Desviaciones', lang) : t('Deviation', 'Desviación', lang)}
            </span>
          )}
          <span className="text-muted no-print" style={{ fontSize: 12 }}>
            {t('Filled by:', 'Completado por:', lang)} {log.FilledBy ? displayName(log.FilledBy) : '—'}
            {log.submittedAt && <> · {new Date(log.submittedAt).toLocaleString()}</>}
            {log.QaReviewedBy && <> · {t('Verified by:', 'Verificado por:', lang)} {displayName(log.QaReviewedBy)}</>}
          </span>
        </div>

        {log.qaNotes && (
          <div className={`alert ${log.qaStatus === 'rejected' ? 'alert-danger' : 'alert-info'} py-2 mb-3`}>
            <strong>{t('QA Notes:', 'Notas QA:', lang)}</strong> {log.qaNotes}
          </div>
        )}

        {/* ── Responses by section ──────────────────────────────────────────── */}
        {(schema.sections || []).map(section => (
          <div key={section.id} className="card mb-3 shadow-sm print-section">
            {section.title && (
              <div className="card-header py-2 fw-semibold" style={{ fontSize: 14 }}>{section.title}</div>
            )}
            <div className="card-body py-2 px-0">
              <table className="table table-sm mb-0">
                <tbody>
                  {(section.fields || []).map(field => {
                    const resp = (log.Responses || []).find(r => r.fieldId === field.id);
                    const isDeviation = resp?.isDeviation;

                    // Multi-reading: parse JSON array of {value,at,by} or plain strings
                    let multiReadings = null;
                    if (field.multipleReadings && resp?.valueText) {
                      try {
                        const parsed = JSON.parse(resp.valueText);
                        if (Array.isArray(parsed)) {
                          multiReadings = parsed
                            .map(r => typeof r === 'object' && r !== null ? r : { value: String(r ?? ''), at: null, byName: null })
                            .filter(r => r.value !== '' && r.value !== null && r.value !== undefined);
                        }
                      } catch { /* ignore */ }
                    }

                    // Item Count List: parse JSON array of {itemName,count,previousCount,isDeviation,deviationNote}
                    let itemCountListRows = null;
                    if (field.type === 'item_count_list' && resp?.valueText) {
                      try {
                        const parsed = JSON.parse(resp.valueText);
                        if (Array.isArray(parsed)) itemCountListRows = parsed;
                      } catch { /* ignore */ }
                    }

                    // Visual Inspection: parse JSON { photoKey, referenceImageUrl, criteria: [{key,label,aiFinding,aiStatus,aiConfidence,qaComment,qaOverride}] }
                    let visualInspectionData = null;
                    if (field.type === 'visual_inspection' && resp?.valueText) {
                      try {
                        const parsed = JSON.parse(resp.valueText);
                        if (parsed && Array.isArray(parsed.criteria)) visualInspectionData = parsed;
                      } catch { /* ignore */ }
                    }

                    // Audit stamp for single-value fields
                    const respondedBy = resp?.RespondedBy;
                    const respondedAt = resp?.respondedAt;
                    const auditStamp = respondedAt
                      ? `${displayName(respondedBy)} · ${new Date(respondedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`
                      : null;

                    return (
                      <tr key={field.id} className={`print-field-row${isDeviation ? ' table-warning' : ''}`}>
                        <td style={{ fontSize: 13, width: '45%', paddingLeft: 12 }}>
                          {field.label}
                          {field.required && <span className="text-danger ms-1">*</span>}
                        </td>
                        <td style={{ fontSize: 13 }}>
                          {visualInspectionData ? (
                            <div>
                              {visualInspectionData.photoKey && (
                                <div className="mb-2"><VisualInspectionPhoto photoKey={visualInspectionData.photoKey} /></div>
                              )}
                              <table className="table table-sm mb-0" style={{ fontSize: 12 }}>
                                <thead>
                                  <tr className="text-muted">
                                    <th style={{ fontWeight: 500 }}>{t('Criterion', 'Criterio', lang)}</th>
                                    <th style={{ fontWeight: 500 }}>{t('AI Finding', 'Hallazgo IA', lang)}</th>
                                    <th style={{ fontWeight: 500 }}>{t('QA Comment', 'Comentario QA', lang)}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {visualInspectionData.criteria.map((c, i) => {
                                    const weightOutOfTolerance = c.key === 'weight' && c.withinTolerance === false;
                                    return (
                                      <tr key={i} className={(c.aiStatus === 'flag' || weightOutOfTolerance) ? 'table-warning' : ''}>
                                        <td>
                                          {c.label}
                                          <span className={`badge ms-1 bg-${c.aiStatus === 'pass' ? 'success' : c.aiStatus === 'flag' ? 'danger' : 'secondary'}`} style={{ fontSize: 9 }}>
                                            {c.aiStatus}
                                          </span>
                                          {c.qaOverride && <span className="badge ms-1 bg-info text-dark" style={{ fontSize: 9 }}>{c.qaOverride}</span>}
                                        </td>
                                        <td className="text-muted">
                                          {c.aiFinding}
                                          {c.key === 'weight' && c.diffPercent != null && (
                                            <div className="mt-1">
                                              <span className="text-dark">{t('Target', 'Objetivo', lang)}: {c.targetValue}{c.targetUnit}</span>
                                              {' · '}
                                              <span className="text-dark">{t('Actual', 'Real', lang)}: {c.actualValue}{c.targetUnit}</span>
                                              {' '}
                                              <span className={`badge bg-${c.withinTolerance ? 'success' : 'danger'}`} style={{ fontSize: 9 }}>
                                                {c.withinTolerance ? '✓' : '✗'} {c.diffPercent}%
                                              </span>
                                            </div>
                                          )}
                                        </td>
                                        <td className="text-muted">{c.qaComment || ''}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          ) : itemCountListRows ? (
                            <table className="table table-sm mb-0" style={{ fontSize: 12 }}>
                              <thead>
                                <tr className="text-muted">
                                  <th style={{ fontWeight: 500 }}>{t('Item', 'Artículo', lang)}</th>
                                  <th style={{ fontWeight: 500 }}>{t('Count', 'Conteo', lang)}</th>
                                  <th style={{ fontWeight: 500 }}>{t('Last Time', 'Última Vez', lang)}</th>
                                  <th style={{ fontWeight: 500 }}>{t('Note', 'Nota', lang)}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {itemCountListRows.map((r, i) => (
                                  <tr key={i} className={r.isDeviation ? 'table-warning' : ''}>
                                    <td>{r.itemName}</td>
                                    <td>{r.count ?? '—'}{r.isDeviation && <span className="ms-1" title="Changed from last time">⚠</span>}</td>
                                    <td className="text-muted">{r.previousCount ?? '—'}</td>
                                    <td className="text-muted">{r.deviationNote || ''}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : multiReadings ? (
                            <div className="d-flex flex-column gap-1">
                              {multiReadings.map((r, i) => {
                                // Highlight the specific out-of-range reading by matching its value
                                // against the deviationReason string (e.g. "Reading 50 is below minimum 80")
                                const deviationReason = resp?.deviationReason || '';
                                const readingFlagged = isDeviation && deviationReason.startsWith(`Reading ${r.value} `);
                                return (
                                  <div key={i}>
                                    <span className={`me-1${readingFlagged ? ' text-danger fw-semibold' : ''}`}>
                                      #{i + 1} {r.value}{field.unit ? ` ${field.unit}` : ''}
                                      {readingFlagged && <span className="ms-1" title={deviationReason}>⚠</span>}
                                    </span>
                                    {r.at && (
                                      <span className="text-muted" style={{ fontSize: 10 }}>
                                        {r.byName || t('Unknown', 'Desconocido', lang)} · {new Date(r.at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                              {isDeviation && resp?.deviationReason && (
                                <div className="text-danger mt-1" style={{ fontSize: 11 }}>⚠ {resp.deviationReason}</div>
                              )}
                            </div>
                          ) : (
                            resp?.valueText ?? <span className="text-muted">—</span>
                          )}
                          {isDeviation && !multiReadings && !itemCountListRows && !visualInspectionData && <span className="badge bg-warning text-dark ms-2" style={{ fontSize: 10 }}>⚠ {t('Dev.', 'Desv.', lang)}</span>}
                          {isDeviation && !multiReadings && !itemCountListRows && !visualInspectionData && resp?.deviationReason && (
                            <div className="text-muted" style={{ fontSize: 11 }}>{resp.deviationReason}</div>
                          )}
                        </td>
                        <td style={{ fontSize: 10, color: '#999', verticalAlign: 'middle' }} className="no-print">
                          {!multiReadings && auditStamp}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))}

        {/* ── Corrective Actions ────────────────────────────────────────────── */}
        {cas.length > 0 && (
          <div className="card mb-3 shadow-sm print-section">
            <div className="card-header py-2 fw-semibold" style={{ fontSize: 14 }}>{t('Corrective Actions', 'Acciones Correctivas', lang)}</div>
            <div className="card-body py-2 px-3">
              {cas.map(ca => {
                const field = allFields.find(f => f.id === ca.fieldId);
                const isVerified = ca.status === 'verified';
                return (
                  <div key={ca.id} className="border rounded p-2 mb-2 print-field-row">
                    {/* Description */}
                    <div className="d-flex justify-content-between align-items-start">
                      <div style={{ flex: 1 }}>
                        {field && <div className="text-muted" style={{ fontSize: 11 }}>{t('Field:', 'Campo:', lang)} {field.label}</div>}
                        <div style={{ fontSize: 13 }}>{ca.description}</div>
                        {ca.rootCause && <div className="text-muted mt-1" style={{ fontSize: 12 }}><strong>{t('Root cause:', 'Causa raíz:', lang)}</strong> {ca.rootCause}</div>}
                        {ca.preventativeMeasure && <div className="text-muted" style={{ fontSize: 12 }}><strong>{t('Preventative:', 'Preventiva:', lang)}</strong> {ca.preventativeMeasure}</div>}
                      </div>
                      {!isVerified && (
                        <div className="ms-2 no-print">
                          <button className="btn btn-outline-primary btn-sm" onClick={() => handleVerify(ca.id)}>
                            ✓ {t('Verify', 'Verificar', lang)}
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Audit trail */}
                    <div className="mt-2 d-flex flex-column gap-1" style={{ fontSize: 11 }}>
                      {/* Step 1 — Entered & resolved */}
                      <div className="d-flex align-items-center gap-1">
                        <span className="text-success">✓</span>
                        <span>
                          <strong>{t('Entered by', 'Ingresado por', lang)}</strong> {ca.CreatedBy ? displayName(ca.CreatedBy) : '—'}
                          {ca.createdAt && (
                            <span className="text-muted ms-1">
                              · {new Date(ca.createdAt).toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                        </span>
                      </div>
                      {/* Step 2 — QA verification */}
                      <div className="d-flex align-items-center gap-1">
                        {isVerified ? (
                          <>
                            <span className="text-success">✓</span>
                            <span>
                              <strong>{t('Verified by', 'Verificado por', lang)}</strong> {ca.VerifiedBy ? displayName(ca.VerifiedBy) : '—'}
                              {ca.verifiedAt && (
                                <span className="text-muted ms-1">
                                  · {new Date(ca.verifiedAt).toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                </span>
                              )}
                            </span>
                          </>
                        ) : (
                          <>
                            <span className="text-muted">○</span>
                            <span className="text-muted">{t('Pending QA verification', 'Pendiente de verificación QA', lang)}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Print footer: Filled By + Verified By ─────────────────────────── */}
        <div id="print-footer" className="border rounded p-3 mt-4">
          <div className="row g-4">
            {/* Filled By */}
            <div className="col-6">
              <div style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6c757d', marginBottom: 6 }}>
                {t('Filled By — Electronic Signature', 'Completado por — Firma Electrónica', lang)}
              </div>
              {log.FilledBy ? (
                <div
                  style={{
                    fontFamily: "'Dancing Script', 'Brush Script MT', cursive",
                    fontSize: '1.6rem',
                    color: '#1a1a6e',
                    lineHeight: 1.2,
                    borderBottom: '1px solid #999',
                    paddingBottom: 4,
                    marginBottom: 4,
                  }}
                >
                  {displayName(log.FilledBy)}
                </div>
              ) : (
                <div style={{ borderBottom: '1px solid #aaa', height: 38, marginBottom: 4 }} />
              )}
              <div style={{ fontSize: '0.7rem', color: '#6c757d' }}>
                {log.FilledBy ? displayName(log.FilledBy) : '—'}
                {log.submittedAt && <> &nbsp;|&nbsp; {new Date(log.submittedAt).toLocaleString()}</>}
              </div>
            </div>

            {/* Verified By / QA */}
            <div className="col-6">
              <div style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6c757d', marginBottom: 6 }}>
                {t('Verified By (QA) — Electronic Signature', 'Verificado por (QA) — Firma Electrónica', lang)}
              </div>
              {log.QaReviewedBy ? (
                <div
                  style={{
                    fontFamily: "'Dancing Script', 'Brush Script MT', cursive",
                    fontSize: '1.6rem',
                    color: '#1a1a6e',
                    lineHeight: 1.2,
                    borderBottom: '1px solid #999',
                    paddingBottom: 4,
                    marginBottom: 4,
                  }}
                >
                  {displayName(log.QaReviewedBy)}
                </div>
              ) : (
                <div style={{ borderBottom: '1px solid #aaa', height: 38, marginBottom: 4 }} />
              )}
              <div style={{ fontSize: '0.7rem', color: '#6c757d' }}>
                {log.QaReviewedBy ? (
                  <>{displayName(log.QaReviewedBy)}{log.qaReviewedAt && <> &nbsp;|&nbsp; {new Date(log.qaReviewedAt).toLocaleString()}</>}</>
                ) : (
                  t('Not yet verified', 'Aún no verificado', lang)
                )}
              </div>
            </div>
          </div>
        </div>

            </td>
          </tr>
        </tbody>
      </table>

      {/* ── QA Review panel (screen only) ─────────────────────────────────────── */}
      {log.qaStatus === 'pending' && log.submittedAt && (
        <div className="card mb-3 shadow-sm no-print mt-4">
          <div className="card-header py-2 fw-semibold" style={{ fontSize: 14 }}>{t('QA Review', 'Revisión QA', lang)}</div>
          <div className="card-body py-2 px-3">

            {/* Gate warning */}
            {approveBlocked && (
              <div className="alert alert-warning py-2 mb-3 d-flex align-items-start gap-2" style={{ fontSize: 13 }}>
                <span>⚠</span>
                <div>
                  <strong>{t('Cannot approve yet.', 'No se puede aprobar aún.', lang)}</strong> {approveBlockReason}
                  {cas.length === 0 && (
                    <div className="mt-1">
                      {t(
                        'Use the',
                        'Use el botón',
                        lang
                      )}{' '}
                      <strong>{t('"+ Add Corrective Action"', '"+ Agregar Acción Correctiva"', lang)}</strong>{' '}
                      {t('button below to document what was done.', 'a continuación para documentar lo que se realizó.', lang)}
                    </div>
                  )}
                </div>
              </div>
            )}

            {qaAction ? (
              <>
                <div className="mb-2">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                    {t('QA Notes', 'Notas QA', lang)} {qaAction === 'reject' && <span className="text-danger">*</span>}
                  </label>
                  <textarea className="form-control form-control-sm" rows={2} value={qaNotes}
                    onChange={e => setQaNotes(e.target.value)}
                    placeholder={qaAction === 'reject'
                      ? t('Explain why this log is being rejected…', 'Explique por qué se está rechazando este registro…', lang)
                      : t('Optional notes…', 'Notas opcionales…', lang)
                    } />
                </div>
                <div className="d-flex gap-2">
                  <button className="btn btn-secondary btn-sm" onClick={() => { setQaAction(null); setQaNotes(''); }}>{t('Cancel', 'Cancelar', lang)}</button>
                  <button className={`btn btn-sm ${qaAction === 'approve' ? 'btn-success' : 'btn-danger'}`}
                    onClick={handleQaSubmit}
                    disabled={qaSubmitting || (qaAction === 'reject' && !qaNotes.trim()) || (qaAction === 'approve' && approveBlocked)}>
                    {qaSubmitting ? '…' : qaAction === 'approve' ? t('Confirm Approve', 'Confirmar Aprobación', lang) : t('Confirm Reject', 'Confirmar Rechazo', lang)}
                  </button>
                </div>
              </>
            ) : (
              <div className="d-flex gap-2">
                <button className="btn btn-success btn-sm" onClick={() => setQaAction('approve')} disabled={approveBlocked} title={approveBlocked ? approveBlockReason : ''}>
                  {t('Approve', 'Aprobar', lang)}
                </button>
                <button className="btn btn-danger btn-sm" onClick={() => setQaAction('reject')}>{t('Reject', 'Rechazar', lang)}</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Add corrective action (screen only) ───────────────────────────────── */}
      {log.hasDeviation && (
        <div className="no-print mb-3">
          {!showCaForm ? (
            <button className="btn btn-outline-warning btn-sm" onClick={() => setShowCaForm(true)}>+ {t('Add Corrective Action', 'Agregar Acción Correctiva', lang)}</button>
          ) : (
            <div className="card shadow-sm">
              <div className="card-header py-2 fw-semibold" style={{ fontSize: 14 }}>{t('New Corrective Action', 'Nueva Acción Correctiva', lang)}</div>
              <div className="card-body py-2 px-3">
                <div className="mb-2">
                  <label className="form-label mb-1" style={{ fontSize: 13 }}>{t('Related Field (optional)', 'Campo relacionado (opcional)', lang)}</label>
                  <select className="form-select form-select-sm" value={caFieldId} onChange={e => setCaFieldId(e.target.value)}>
                    <option value="">{t('None', 'Ninguno', lang)}</option>
                    {deviations.map(d => <option key={d.fieldId} value={d.fieldId}>{d.fieldLabel}</option>)}
                  </select>
                </div>
                <div className="mb-2">
                  <label className="form-label mb-1" style={{ fontSize: 13 }}>{t('Description', 'Descripción', lang)} <span className="text-danger">*</span></label>
                  <textarea className="form-control form-control-sm" rows={2} value={caDescription} onChange={e => setCaDescription(e.target.value)} />
                </div>
                <div className="mb-2">
                  <label className="form-label mb-1" style={{ fontSize: 13 }}>{t('Root Cause', 'Causa Raíz', lang)}</label>
                  <textarea className="form-control form-control-sm" rows={2} value={caRootCause} onChange={e => setCaRootCause(e.target.value)} />
                </div>
                <div className="mb-2">
                  <label className="form-label mb-1" style={{ fontSize: 13 }}>{t('Preventative Measure', 'Medida Preventiva', lang)}</label>
                  <textarea className="form-control form-control-sm" rows={2} value={caPreventative} onChange={e => setCaPreventative(e.target.value)} />
                </div>
                <div className="d-flex gap-2">
                  <button className="btn btn-secondary btn-sm" onClick={() => setShowCaForm(false)}>{t('Cancel', 'Cancelar', lang)}</button>
                  <button className="btn btn-warning btn-sm" onClick={handleCaSubmit} disabled={caSubmitting || !caDescription.trim()}>
                    {caSubmitting ? '…' : t('Save Corrective Action', 'Guardar Acción Correctiva', lang)}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
